import React, { useCallback, useEffect, useRef, useState } from "react";
import { DecoratorNode, LexicalEditor, $getNodeByKey } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

// ═══════════════════════════════════════════════════════
// IMAGE NODE — custom Lexical node for inline images
// ═══════════════════════════════════════════════════════
export class ImageNode extends DecoratorNode<React.ReactElement> {
  __src: string;
  __altText: string;
  __width: number | "inherit";
  __height: number | "inherit";

  static getType(): string {
    return "image";
  }
  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  constructor(src: string, altText: string, width?: number | "inherit", height?: number | "inherit", key?: string) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width || "inherit";
    this.__height = height || "inherit";
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "editor-image";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(data: any): ImageNode {
    return new ImageNode(data.src, data.altText, data.width, data.height);
  }

  exportJSON() {
    return {
      type: "image",
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
    };
  }

  decorate(): React.ReactElement {
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        style={{
          maxWidth: "100%",
          width: this.__width !== "inherit" ? this.__width : undefined,
          height: this.__height !== "inherit" ? this.__height : undefined,
        }}
        draggable={false}
      />
    );
  }
}

export function $createImageNode(src: string, altText: string): ImageNode {
  return new ImageNode(src, altText);
}

// ═══════════════════════════════════════════════════════
// FLOATING IMAGE NODE — absolutely positioned, draggable over text
// ═══════════════════════════════════════════════════════
export class FloatingImageNode extends DecoratorNode<React.ReactElement> {
  __src: string;
  __x: number;
  __y: number;
  __w: number;
  __h: number;

  static getType(): string {
    return "floating-image";
  }
  static clone(node: FloatingImageNode): FloatingImageNode {
    return new FloatingImageNode(node.__src, node.__x, node.__y, node.__w, node.__h, node.__key);
  }

  constructor(src: string, x: number, y: number, w: number, h: number, key?: string) {
    super(key);
    this.__src = src;
    this.__x = x;
    this.__y = y;
    this.__w = w;
    this.__h = h;
  }

  createDOM(): HTMLElement {
    const el = document.createElement("div");
    el.style.position = "absolute";
    el.style.left = this.__x + "px";
    el.style.top = this.__y + "px";
    el.style.zIndex = "50";
    el.style.pointerEvents = "auto";
    el.className = "floating-image-wrapper";
    return el;
  }

  updateDOM(prev: FloatingImageNode, dom: HTMLElement): boolean {
    if (prev.__x !== this.__x) dom.style.left = this.__x + "px";
    if (prev.__y !== this.__y) dom.style.top = this.__y + "px";
    return false;
  }

  setPosition(x: number, y: number): void {
    const self = this.getWritable();
    self.__x = x;
    self.__y = y;
  }

  static importJSON(data: any): FloatingImageNode {
    return new FloatingImageNode(data.src, data.x, data.y, data.w, data.h);
  }

  exportJSON() {
    return { type: "floating-image", version: 1, src: this.__src, x: this.__x, y: this.__y, w: this.__w, h: this.__h };
  }

  isInline(): boolean {
    return false;
  }
  isIsolated(): boolean {
    return true;
  }

  decorate(_editor: LexicalEditor): React.ReactElement {
    return <DraggableFloatingImage nodeKey={this.__key} src={this.__src} x={this.__x} y={this.__y} w={this.__w} h={this.__h} />;
  }
}

export function $createFloatingImageNode(src: string, x: number, y: number, w: number, h: number): FloatingImageNode {
  return new FloatingImageNode(src, x, y, w, h);
}

// Draggable wrapper component for floating images
function DraggableFloatingImage({
  nodeKey,
  src,
  x,
  y,
  w,
  h,
}: {
  nodeKey: string;
  src: string;
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  const [editor] = useLexicalComposerContext();
  const imgRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ dx: 0, dy: 0 });

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const editorRoot = editor.getRootElement();
      if (!editorRoot) return;
      const rootRect = editorRoot.getBoundingClientRect();
      dragOffset.current = { dx: e.clientX - (rootRect.left + x), dy: e.clientY - (rootRect.top + y - editorRoot.scrollTop) };
      setDragging(true);
    },
    [editor, x, y],
  );

  useEffect(() => {
    if (!dragging) return;
    const editorRoot = editor.getRootElement();
    if (!editorRoot) return;

    const onMove = (e: MouseEvent) => {
      const rootRect = editorRoot.getBoundingClientRect();
      const newX = Math.max(0, e.clientX - rootRect.left - dragOffset.current.dx);
      const newY = Math.max(0, e.clientY - rootRect.top + editorRoot.scrollTop - dragOffset.current.dy);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node instanceof FloatingImageNode) node.setPosition(newX, newY);
      });
    };

    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, editor, nodeKey]);

  const onDelete = useCallback(() => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) node.remove();
    });
  }, [editor, nodeKey]);

  return (
    <div
      ref={imgRef}
      className={`floating-image${dragging ? " dragging" : ""}`}
      onMouseDown={onMouseDown}
      style={{ width: w, height: h, cursor: dragging ? "grabbing" : "grab" }}
    >
      <img src={src} alt="stamp" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} draggable={false} />
      <button className="floating-image-delete" onClick={onDelete} title="Remove">
        &times;
      </button>
    </div>
  );
}
